import React from 'react';
import SEO from '../components/SEO';
import GamifiedButton from '../components/common/GamifiedButton';
import { useLang } from '../i18n/LanguageContext';


export default function About() {
  const { t } = useLang();

  return (
    <div className="bg-[#FEFEFA]">
      <SEO
        title={t('apropos.seo.titre')}
        description={t('apropos.seo.description')}
        url="/about"
        keywords="Mirum Games, histoire, jeu gender reveal, révélation bébé, jeu de fête"
      />

      {/* Header */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-gradient-to-br from-[#F1F2E9] via-[#F2EFE4] to-[#FEFEFA] border-b-4 border-[#DCEAE4] text-center">
        <div className="max-w-3xl mx-auto flex flex-col gap-6 animate-[fadeInUp_0.8s_ease-out]">
          <span className="flex items-center justify-center bg-gradient-to-br from-[#A8D5BA]/20 to-[#F4A6C1]/20 border-2 border-[#A8D5BA] text-[#17453E] py-3 md:py-4 px-4 md:px-6 rounded-2xl text-xs font-bold uppercase tracking-widest w-fit mx-auto">
            {t('apropos.hero.badge')}
          </span>
          <h1 className="m-0 text-3xl sm:text-4xl md:text-5xl font-black break-words text-[#1F3833] uppercase tracking-tight font-['Fredoka',_'Poppins',_sans-serif] leading-tight">
            {t('apropos.hero.titre')}
          </h1>
          <p className="m-0 text-lg text-[#1F3833] leading-relaxed">
            {t('apropos.hero.texte')}
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-gradient-to-br from-[#FEFEFA] to-[#F2EFE4]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="flex flex-col gap-6 animate-[fadeInUp_0.8s_ease-out]">
              <h2 className="m-0 text-3xl md:text-4xl font-black text-[#1F3833] uppercase tracking-tight font-['Fredoka',_'Poppins',_sans-serif]">
                {t('apropos.mission.titre')}
              </h2>
              <p className="m-0 text-base leading-relaxed text-[#1F3833]">
                {t('apropos.mission.texte1')}
              </p>
              <p className="m-0 text-base leading-relaxed text-[#1F3833]">
                {t('apropos.mission.texte2')}
              </p>
              <div className="flex items-start gap-4 p-6 bg-gradient-to-br from-[#A8D5BA]/10 to-[#F4A6C1]/10 border-2 border-[#A8D5BA] rounded-2xl">
                <span className="text-4xl flex-shrink-0">💡</span>
                <div>
                  <h3 className="m-0 mb-2 text-lg font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif]">
                    {t('apropos.mission.encart.titre')}
                  </h3>
                  <p className="m-0 text-sm text-[#2F7E72]">
                    {t('apropos.mission.encart.texte')}
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 animate-[fadeInUp_0.8s_ease-out_0.2s_backwards]">
              <div className="p-6 md:p-8 bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl shadow-[0_10px_40px_rgba(139,111,71,0.15)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(139,111,71,0.25)]">
                <div className="text-5xl mb-4">📚</div>
                <h3 className="m-0 mb-2 text-base font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif] uppercase tracking-wide">
                  {t('apropos.mission.carte1.titre')}
                </h3>
                <p className="m-0 text-sm text-[#2F7E72]">{t('apropos.mission.carte1.texte')}</p>
              </div>
              <div className="p-6 md:p-8 bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl shadow-[0_10px_40px_rgba(139,111,71,0.15)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(139,111,71,0.25)]">
                <div className="text-5xl mb-4">🎊</div>
                <h3 className="m-0 mb-2 text-base font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif] uppercase tracking-wide">
                  {t('apropos.mission.carte2.titre')}
                </h3>
                <p className="m-0 text-sm text-[#2F7E72]">{t('apropos.mission.carte2.texte')}</p>
              </div>
              <div className="p-6 md:p-8 bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl shadow-[0_10px_40px_rgba(139,111,71,0.15)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(139,111,71,0.25)]">
                <div className="text-5xl mb-4">👨‍👩‍👧‍👦</div>
                <h3 className="m-0 mb-2 text-base font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif] uppercase tracking-wide">
                  {t('apropos.mission.carte3.titre')}
                </h3>
                <p className="m-0 text-sm text-[#2F7E72]">{t('apropos.mission.carte3.texte')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-gradient-to-br from-[#F1F2E9] to-[#F2EFE4] border-t-4 border-[#DCEAE4]">
        <div className="max-w-6xl mx-auto">
          <h2 className="m-0 mb-4 text-3xl sm:text-4xl md:text-5xl font-black break-words text-[#1F3833] uppercase tracking-tight text-center font-['Fredoka',_'Poppins',_sans-serif]">
            {t('apropos.valeurs.titre')}
          </h2>
          <p className="m-0 mb-12 md:mb-16 text-lg text-[#2F7E72] text-center italic">
            {t('apropos.valeurs.soustitre')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[
              { icon: '🎯', title: t('apropos.valeurs.excellence.titre'), text: t('apropos.valeurs.excellence.texte'), delay: '0.1s' },
              { icon: '🎉', title: t('apropos.valeurs.creativite.titre'), text: t('apropos.valeurs.creativite.texte'), delay: '0.2s' },
              { icon: '🤝', title: t('apropos.valeurs.authenticite.titre'), text: t('apropos.valeurs.authenticite.texte'), delay: '0.3s' },
              { icon: '🚀', title: t('apropos.valeurs.innovation.titre'), text: t('apropos.valeurs.innovation.texte'), delay: '0.4s' },
              { icon: '💝', title: t('apropos.valeurs.emotions.titre'), text: t('apropos.valeurs.emotions.texte'), delay: '0.5s' },
              { icon: '❤️', title: t('apropos.valeurs.passion.titre'), text: t('apropos.valeurs.passion.texte'), delay: '0.6s' }
            ].map((value, index) => (
              <div key={index} className="p-6 md:p-8 bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl shadow-[0_10px_40px_rgba(139,111,71,0.15)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(139,111,71,0.25)] animate-[fadeInUp_0.6s_ease-out_backwards]" style={{ animationDelay: value.delay }}>
                <div className="text-5xl mb-4">{value.icon}</div>
                <h3 className="m-0 mb-4 text-base font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif] uppercase tracking-wide">
                  {value.title}
                </h3>
                <p className="m-0 text-sm leading-relaxed text-[#1F3833]">
                  {value.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Section */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-gradient-to-br from-[#FEFEFA] to-[#F2EFE4]">
        <div className="max-w-6xl mx-auto">
          <h2 className="m-0 mb-4 text-3xl sm:text-4xl md:text-5xl font-black break-words text-[#1F3833] uppercase tracking-tight text-center font-['Fredoka',_'Poppins',_sans-serif]">
            {t('apropos.reseaux.titre')}
          </h2>
          <p className="m-0 mb-12 md:mb-16 text-lg text-[#2F7E72] text-center italic">
            {t('apropos.reseaux.soustitre')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <a
              href="https://www.instagram.com/mirumgames/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-6 p-8 md:p-10 bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl shadow-[0_10px_40px_rgba(139,111,71,0.15)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(139,111,71,0.25)] hover:border-[#E1A72E] animate-[fadeInUp_0.6s_ease-out_backwards] [animation-delay:0.1s] no-underline group">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#4AA294] to-[#2F7E72] rounded-full transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.057-1.645.069-4.849.069-3.205 0-3.584-.012-4.849-.069-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.322a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z" />
                </svg>
              </div>
              <h3 className="m-0 text-2xl font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif]">
                Instagram
              </h3>
              <p className="m-0 text-sm text-center leading-relaxed text-[#2F7E72]">
                {t('apropos.reseaux.instagram.texte')}
              </p>
              <span className="mt-2 text-[#E1A72E] font-semibold text-sm uppercase tracking-wide group-hover:text-[#2F7E72] transition-colors">
                @mirumgames →
              </span>
            </a>

            <a
              href="https://www.facebook.com/people/MirumGames/61553240464727/?locale=fr_FR"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-6 p-8 md:p-10 bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl shadow-[0_10px_40px_rgba(139,111,71,0.15)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(139,111,71,0.25)] hover:border-[#E1A72E] animate-[fadeInUp_0.6s_ease-out_backwards] [animation-delay:0.2s] no-underline group">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#4AA294] to-[#2F7E72] rounded-full transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </div>
              <h3 className="m-0 text-2xl font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif]">
                Facebook
              </h3>
              <p className="m-0 text-sm text-center leading-relaxed text-[#2F7E72]">
                {t('apropos.reseaux.facebook.texte')}
              </p>
              <span className="mt-2 text-[#E1A72E] font-semibold text-sm uppercase tracking-wide group-hover:text-[#2F7E72] transition-colors">
                MirumGames →
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-gradient-to-br from-[#FEFEFA] to-[#F2EFE4]">
        <div className="max-w-6xl mx-auto">
          <h2 className="m-0 mb-4 text-3xl sm:text-4xl md:text-5xl font-black break-words text-[#1F3833] uppercase tracking-tight text-center font-['Fredoka',_'Poppins',_sans-serif]">
            {t('apropos.processus.titre')}
          </h2>
          <p className="m-0 mb-12 md:mb-16 text-lg text-[#2F7E72] text-center italic">
            {t('apropos.processus.soustitre')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-start">
            <div className="flex flex-col p-6 md:p-8 bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl shadow-[0_10px_40px_rgba(139,111,71,0.15)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(139,111,71,0.25)] animate-[fadeInUp_0.6s_ease-out_backwards] h-full" style={{ animationDelay: '0.1s' }}>
              <div className="text-2xl font-black text-[#E1A72E] mb-4 font-['Fredoka',_'Poppins',_sans-serif]">
                01
              </div>
              <h3 className="m-0 mb-4 text-base font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif] uppercase tracking-wide">
                {t('apropos.processus.etape1.titre')}
              </h3>
              <p className="m-0 text-sm leading-relaxed text-[#2F7E72] flex-1">
                {t('apropos.processus.etape1.texte')}
              </p>
            </div>

            <div className="flex flex-col p-6 md:p-8 bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl shadow-[0_10px_40px_rgba(139,111,71,0.15)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(139,111,71,0.25)] animate-[fadeInUp_0.6s_ease-out_backwards] h-full" style={{ animationDelay: '0.2s' }}>
              <div className="text-2xl font-black text-[#E1A72E] mb-4 font-['Fredoka',_'Poppins',_sans-serif]">
                02
              </div>
              <h3 className="m-0 mb-4 text-base font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif] uppercase tracking-wide">
                {t('apropos.processus.etape2.titre')}
              </h3>
              <p className="m-0 text-sm leading-relaxed text-[#2F7E72] flex-1">
                {t('apropos.processus.etape2.texte')}
              </p>
            </div>

            <div className="flex flex-col p-6 md:p-8 bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl shadow-[0_10px_40px_rgba(139,111,71,0.15)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(139,111,71,0.25)] animate-[fadeInUp_0.6s_ease-out_backwards] h-full" style={{ animationDelay: '0.3s' }}>
              <div className="text-2xl font-black text-[#E1A72E] mb-4 font-['Fredoka',_'Poppins',_sans-serif]">
                03
              </div>
              <h3 className="m-0 mb-4 text-base font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif] uppercase tracking-wide">
                {t('apropos.processus.etape3.titre')}
              </h3>
              <p className="m-0 text-sm leading-relaxed text-[#2F7E72] flex-1">
                {t('apropos.processus.etape3.texte')}
              </p>
            </div>

            <div className="flex flex-col p-6 md:p-8 bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl shadow-[0_10px_40px_rgba(139,111,71,0.15)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(139,111,71,0.25)] animate-[fadeInUp_0.6s_ease-out_backwards] h-full" style={{ animationDelay: '0.4s' }}>
              <div className="text-2xl font-black text-[#E1A72E] mb-4 font-['Fredoka',_'Poppins',_sans-serif]">
                04
              </div>
              <h3 className="m-0 mb-4 text-base font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif] uppercase tracking-wide">
                {t('apropos.processus.etape4.titre')}
              </h3>
              <p className="m-0 text-sm leading-relaxed text-[#2F7E72] flex-1">
                {t('apropos.processus.etape4.texte')}
              </p>
            </div>
          </div>
        </div>
      </section >

      {/* CTA Section */}
      < section className="py-12 md:py-16 px-4 md:px-8 bg-gradient-to-br from-[#F1F2E9] to-[#F2EFE4] border-t-4 border-[#DCEAE4] text-center" >
        <div className="max-w-3xl mx-auto flex flex-col gap-8 animate-[fadeInUp_0.8s_ease-out]">
          <h2 className="m-0 text-3xl md:text-[40px] font-black text-[#1F3833] uppercase tracking-tight font-['Fredoka',_'Poppins',_sans-serif]">
            {t('apropos.cta.titre')}
          </h2>
          <p className="m-0 text-lg leading-relaxed text-[#1F3833]">
            {t('apropos.cta.texte')}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a href="/shop" className="inline-block">
              <GamifiedButton variant="primary" size="lg">
                {t('apropos.cta.boutique')}
              </GamifiedButton>
            </a>
            <a href="/contact" className="inline-block">
              <GamifiedButton variant="secondary" size="lg">
                {t('apropos.cta.contact')}
              </GamifiedButton>
            </a>
          </div>
        </div>
      </section >
    </div >
  );
}
